import React, { useMemo } from "react";
import PropTypes from "prop-types";
import * as R from "ramda";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Modal, Icon, notification, Menu, Dropdown } from "antd";
import { Query } from "react-apollo";
import gql from "graphql-tag";

import Button from "@arachnys/styleguide/Button";
import Table from "@arachnys/styleguide/Table";
import { withApolloState } from "@arachnys/flow-app/src/util";
import { Fetch } from "../../api.js";
import { formatDatetimeString } from "../../formatters";
import ProcessSummary from "../ProcessSummary";

import styles from "./styles";

const renderCurrentTaskTitle = current_task => {
    if (!current_task) {
        return "";
    }

    if (current_task.assignable) {
        return (
            <Link to={`/tasks/${current_task.uid}/claim/`}>
                {current_task.title}
            </Link>
        );
    }

    return current_task.title;
};

export const ProcessesMenu = ({ links, startProcess }) => {
    const externalLinks = useMemo(() => {
        const startsWith = R.invoker(1, "startsWith");
        const isLink = startsWith("https://");
        const getExternalLinks = R.filter(R.pipe(R.prop("link"), isLink));
        return getExternalLinks(links || []);
    }, [links]);

    const processLinks = useMemo(() => {
        return R.difference(links || [], externalLinks);
    }, [links, externalLinks]);

    const allLinks = useMemo(
        () => [
            ...processLinks,
            ...externalLinks.map(l => ({
                ...l,
                isExternalLink: true,
            })),
        ],
        [externalLinks, processLinks]
    );

    return (
        <Menu onClick={startProcess} className={`${styles.processesMenu}`}>
            {allLinks.map(process =>
                process.isExternalLink ? (
                    <Menu.Item
                        data-at-id="process-menu-item"
                        key={process.label}
                        className={`${styles.processesMenuItem}`}
                    >
                        <a
                            href={process.link}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            {process.label}
                        </a>
                    </Menu.Item>
                ) : (
                    <Menu.Item
                        data-at-id="process-menu-item"
                        link={process.link}
                        key={process.label}
                        className={`${styles.processesMenuItem}`}
                    >
                        {process.label}
                    </Menu.Item>
                )
            )}
        </Menu>
    );
};

// TODO: consider Silviu comment on github:
//   Very similar container functionality with EditEntityProcessButton.
//   Maybe in time we can abstract the logic into a parent Process container.
export class StartProcessDropdown extends React.Component {
    static propTypes = {
        links: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                link: PropTypes.string.isRequired,
            })
        ),
        ulid: PropTypes.string.isRequired,
    };

    startProcess = async ({
        item: {
            props: { link },
        },
    }) => {
        if (!link) {
            return false;
        }
        const { ulid, toggleSpinner } = this.props;
        const body = JSON.stringify({
            ulid,
        });
        const request = new Request(link, {
            method: "POST",
            body,
        });

        toggleSpinner();

        try {
            const { next_task_id } = await Fetch(request);
            this.props.history.push(`/tasks/${next_task_id}/claim/`);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
            notification.error({
                message: "Internal server error",
                description:
                    "Sorry, an unexpected error has occurred on our side, and we are looking into it.",
            });
        } finally {
            toggleSpinner();
        }
    };

    render() {
        return !R.isEmpty(this.props.links) && this.props.canEdit ? (
            <Dropdown
                overlay={
                    <ProcessesMenu
                        links={this.props.links}
                        startProcess={this.startProcess}
                    />
                }
                trigger={["click"]}
            >
                <Button.Primary
                    data-at-id="create-research-item-button"
                    size="large"
                    icon="plus-circle"
                >
                    Create Research Item
                </Button.Primary>
            </Dropdown>
        ) : null;
    }
}

const StartProcessDropdownWithRouter = withRouter(StartProcessDropdown);

class ProcessSummaryModal extends React.Component {
    state = {
        processSummaryVisible: false,
    };

    showModal = e => {
        e.preventDefault();
        this.setState({
            modalVisible: true,
        });
    };

    hideModal = e => {
        e.preventDefault();
        this.setState({
            modalVisible: false,
        });
    };

    render() {
        return (
            <div>
                <Icon
                    type="solution"
                    onClick={this.showModal}
                    {...styles.icon}
                />
                <Modal
                    footer={null}
                    onCancel={this.hideModal}
                    title="Process Summary"
                    visible={this.state.modalVisible}
                    width={800}
                >
                    <ProcessSummary processId={this.props.processId} />
                </Modal>
            </div>
        );
    }
}

export default class ProcessesTable extends React.Component {
    columns = entityUlid => [
        {
            id: "title",
            Header: "Process",
            accessor: "title",
        },
        {
            id: "step",
            Header: "Step",
            accessor: e => renderCurrentTaskTitle(e.activeTask),
        },
        {
            id: "created",
            Header: "Start date",
            accessor: e => formatDatetimeString({ str: e.created }),
        },
        {
            id: "finished",
            Header: "Completion date",
            accessor: e => formatDatetimeString({ str: e.finished }),
        },
        {
            id: "summary",
            accessor: e => {
                if (e.title.includes("ACCG")) {
                    return null;
                }
                return <ProcessSummaryModal processId={e.uid} />;
            },
        },
        {
            id: "history",
            Cell: row => {
                return row.original.finished &&
                    row.original.hasProcessHistory ? (
                    <>
                        <Link
                            to={`/entities/${entityUlid}/process-history/${row.original.uid}/`}
                        >
                            History
                        </Link>
                        {row.original.processHistory &&
                            row.original.processHistory.reportUrl && (
                                <>
                                    &nbsp;|&nbsp;
                                    <a
                                        href={
                                            row.original.processHistory
                                                .reportUrl
                                        }
                                    >
                                        Download
                                    </a>
                                </>
                            )}
                    </>
                ) : null;
            },
        },
    ];

    render() {
        const processes = this.props.entity.processes;

        return (
            <>
                <div className={styles.processesHeader}>
                    <div className={styles.processesTitle}>Processes</div>
                    <div className={styles.processesButtonContainer}>
                        <Query
                            variables={{ hookType: 3 }}
                            query={gql`
                                query ProcessesTable_DetailViewEntityActions_Query(
                                    $hookType: Int
                                ) {
                                    clientConfig {
                                        hooks(hookType: $hookType) {
                                            type
                                            label
                                            link
                                            priority
                                        }
                                    }
                                }
                            `}
                        >
                            {withApolloState(({ data }) => {
                                if (!data) {
                                    return null;
                                }
                                return (
                                    <StartProcessDropdownWithRouter
                                        ulid={this.props.entity.key.ulid}
                                        links={data.clientConfig.hooks}
                                        dispatch={this.props.dispatch}
                                        canEdit={this.props.canEdit}
                                        toggleSpinner={this.props.toggleSpinner}
                                    />
                                );
                            })}
                        </Query>
                    </div>
                </div>

                <Table
                    columns={this.columns(this.props.entity.key.ulid)}
                    data={processes}
                    noDataText="No research items found"
                    showPagination
                    minRows={3}
                />
            </>
        );
    }
}
